import { normalizeColorRoute, toColorRouteSlug } from './colorRoute'

type AssetsKitMediaEntry =
  | string
  | {
      url?: string | null
      src?: string | null
    }

interface AssetsKitVariantPayload {
  option1?: string | null
  sku?: string | null
  title?: string | null
  price_amount?: number | null
  inventory_quantity?: number | null
  availability?: string | null
  images?: AssetsKitMediaEntry[] | null
  photos?: AssetsKitMediaEntry[] | null
  photos_zoom?: AssetsKitMediaEntry[] | null
}

interface AssetsKitProductPayload {
  title?: string | null
  slug?: string | null
  sku?: string | null
  description?: string | null
  price_amount?: number | null
  inventory_quantity?: number | null
  availability?: string | null
  images?: AssetsKitMediaEntry[] | null
  variants?: AssetsKitVariantPayload[] | null
}

interface AssetsKitCollectionItemPayload {
  products?: AssetsKitProductPayload | null
  product?: AssetsKitProductPayload | null
  title?: string | null
  slug?: string | null
  sku?: string | null
  description?: string | null
  price_amount?: number | null
  inventory_quantity?: number | null
  availability?: string | null
  images?: AssetsKitMediaEntry[] | null
  variants?: AssetsKitVariantPayload[] | null
}

interface FallbackCard {
  color: string
  title?: string
  image?: string
  description?: string
  price?: number | null
}

export interface CollectionSeoVariant {
  color: string
  colorLabel: string
  title: string
  description: string
  image: string
  images: string[]
  price: number | null
  sku: string | null
  inventoryQuantity: number | null
  availability: string | null
  url: string
}

interface BuildSeoVariantsOptions {
  items: AssetsKitCollectionItemPayload[]
  fallbackCards?: FallbackCard[]
  colorOrder?: string[]
  baseUrl: string
  defaultTitle: string
  defaultDescription?: string
  colorLabel: (color: string) => string
  siteUrl?: string
}

interface BuildStructuredDataOptions {
  brandName?: string
  category?: string
  collectionDescription?: string
  collectionId?: string
  collectionName: string
  priceValidUntil?: string
  selectedVariant?: CollectionSeoVariant | null
  variants: CollectionSeoVariant[]
}

const DEFAULT_SITE_URL = 'https://kendrick.paris'

function buildVariantUrl(baseUrl: string, color: string) {
  return `${baseUrl.replace(/\/+$/, '')}/${toColorRouteSlug(color)}`
}

export function cleanProductSeoText(value?: string | null) {
  return (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizeMediaUrl(entry: AssetsKitMediaEntry | undefined | null, siteUrl: string) {
  const rawValue = typeof entry === 'string' ? entry : entry?.url || entry?.src || ''
  const value = rawValue.trim()

  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  if (value.startsWith('//')) return `https:${value}`
  if (value.startsWith('/')) return `${siteUrl}${value}`

  return `${siteUrl}/${value.replace(/^\/+/, '')}`
}

function getMediaUrls(siteUrl: string, ...groups: Array<AssetsKitMediaEntry[] | null | undefined>) {
  return Array.from(
    new Set(
      groups.flatMap(group =>
        (Array.isArray(group) ? group : [])
          .map(entry => normalizeMediaUrl(entry, siteUrl))
          .filter(Boolean)
      )
    )
  )
}

function resolveCollectionItemProduct(item: AssetsKitCollectionItemPayload | null | undefined) {
  if (!item) return null
  if (item.products) return item.products
  if (item.product) return item.product
  if (Array.isArray(item.variants) || item.slug || item.title || item.sku) {
    return item as AssetsKitProductPayload
  }
  return null
}

function normalizeInventoryQuantity(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function resolveAvailability(availability?: string | null, inventoryQuantity?: number | null) {
  if (typeof inventoryQuantity === 'number') {
    if (inventoryQuantity > 0) return 'https://schema.org/InStock'
    if (inventoryQuantity <= 0) return 'https://schema.org/OutOfStock'
  }

  const normalized = availability?.trim().toLowerCase()
  if (!normalized) return null
  if (normalized.includes('pre')) return 'https://schema.org/PreOrder'
  if (normalized.includes('out')) return 'https://schema.org/OutOfStock'
  if (normalized.includes('in') || normalized.includes('available')) return 'https://schema.org/InStock'

  return null
}

function pruneStructuredData<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map(item => pruneStructuredData(item))
      .filter((item) => {
        if (item == null) return false
        if (Array.isArray(item)) return item.length > 0
        if (typeof item === 'object') return Object.keys(item).length > 0
        return true
      }) as T
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, any>)
      .map(([key, item]) => [key, pruneStructuredData(item)] as const)
      .filter(([, item]) => {
        if (item == null) return false
        if (Array.isArray(item)) return item.length > 0
        if (typeof item === 'object') return Object.keys(item).length > 0
        return item !== ''
      })

    return Object.fromEntries(entries) as T
  }

  return value
}

export function buildMerchantReturnPolicy() {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: 'FR',
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: 14,
    returnMethod: 'https://schema.org/ReturnByMail',
    returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility'
  }
}

export function buildOfferShippingDetails() {
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: 0,
      currency: 'EUR'
    },
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: 'FR'
    },
    deliveryTime: {
      '@type': 'ShippingDeliveryTime',
      handlingTime: {
        '@type': 'QuantitativeValue',
        minValue: 1,
        maxValue: 2,
        unitCode: 'DAY'
      },
      transitTime: {
        '@type': 'QuantitativeValue',
        minValue: 2,
        maxValue: 5,
        unitCode: 'DAY'
      }
    }
  }
}

function buildOffer(variant: CollectionSeoVariant, priceValidUntil?: string) {
  return pruneStructuredData({
    '@type': 'Offer',
    url: variant.url,
    priceCurrency: 'EUR',
    price: typeof variant.price === 'number' && variant.price > 0 ? String(variant.price) : undefined,
    availability: variant.availability || 'https://schema.org/LimitedAvailability',
    itemCondition: 'https://schema.org/NewCondition',
    priceValidUntil,
    hasMerchantReturnPolicy: buildMerchantReturnPolicy(),
    shippingDetails: buildOfferShippingDetails()
  })
}

function buildProductNode(
  variant: CollectionSeoVariant,
  {
    brandName = 'Kendrick Paris',
    category,
    priceValidUntil
  }: Pick<BuildStructuredDataOptions, 'brandName' | 'category' | 'priceValidUntil'>
) {
  return pruneStructuredData({
    '@type': 'Product',
    name: variant.title,
    description: variant.description,
    image: variant.images.length ? variant.images : variant.image ? [variant.image] : undefined,
    sku: variant.sku || undefined,
    color: variant.colorLabel,
    category,
    brand: {
      '@type': 'Brand',
      name: brandName
    },
    url: variant.url,
    offers: buildOffer(variant, priceValidUntil)
  })
}

export function buildAssetsKitProductSeoVariants({
  items,
  fallbackCards = [],
  colorOrder = [],
  baseUrl,
  defaultTitle,
  defaultDescription = '',
  colorLabel,
  siteUrl = DEFAULT_SITE_URL
}: BuildSeoVariantsOptions) {
  const variantsByColor = new Map<string, CollectionSeoVariant>()

  for (const item of items) {
    const product = resolveCollectionItemProduct(item)
    const groupedVariants = Array.isArray(product?.variants) ? product.variants : []

    for (const variant of groupedVariants) {
      const rawColor = variant?.option1?.trim()
      if (!rawColor) continue

      const color = normalizeColorRoute(rawColor)
      if (!color) continue

      const existingVariant = variantsByColor.get(color)
      const variantMedia = getMediaUrls(
        siteUrl,
        variant.images,
        variant.photos,
        variant.photos_zoom
      )
      const productMedia = getMediaUrls(siteUrl, product?.images)
      const media = variantMedia.length ? variantMedia : productMedia
      const description = cleanProductSeoText(defaultDescription) || cleanProductSeoText(product?.description)
      const inventoryQuantity = normalizeInventoryQuantity(
        variant.inventory_quantity ?? product?.inventory_quantity ?? null
      )
      const nextVariant: CollectionSeoVariant = {
        color,
        colorLabel: colorLabel(color),
        title: `${defaultTitle || product?.title || 'Collection product'} - ${colorLabel(color)}`,
        description,
        image: media[0] || '',
        images: media,
        price: variant.price_amount ?? product?.price_amount ?? null,
        sku: variant.sku || product?.sku || null,
        inventoryQuantity,
        availability: resolveAvailability(
          variant.availability ?? product?.availability ?? null,
          inventoryQuantity
        ),
        url: buildVariantUrl(baseUrl, color)
      }

      if (!existingVariant) {
        variantsByColor.set(color, nextVariant)
        continue
      }

      const shouldReplace =
        (!existingVariant.image && !!nextVariant.image) ||
        (!existingVariant.price && !!nextVariant.price) ||
        (!existingVariant.sku && !!nextVariant.sku)

      if (shouldReplace) {
        variantsByColor.set(color, {
          ...existingVariant,
          ...nextVariant
        })
      }
    }
  }

  for (const fallbackCard of fallbackCards) {
    const color = normalizeColorRoute(fallbackCard.color)
    if (!color) continue

    const existingVariant = variantsByColor.get(color)
    const fallbackImages = getMediaUrls(siteUrl, fallbackCard.image ? [fallbackCard.image] : [])

    if (!existingVariant) {
      variantsByColor.set(color, {
        color,
        colorLabel: colorLabel(color),
        title: `${fallbackCard.title || defaultTitle || 'Collection product'} - ${colorLabel(color)}`,
        description: cleanProductSeoText(fallbackCard.description) || cleanProductSeoText(defaultDescription),
        image: fallbackImages[0] || '',
        images: fallbackImages,
        price: fallbackCard.price ?? null,
        sku: null,
        inventoryQuantity: null,
        availability: null,
        url: buildVariantUrl(baseUrl, color)
      })
      continue
    }

    variantsByColor.set(color, {
      ...existingVariant,
      title: existingVariant.title || `${fallbackCard.title || defaultTitle || 'Collection product'} - ${colorLabel(color)}`,
      description: existingVariant.description || cleanProductSeoText(fallbackCard.description) || cleanProductSeoText(defaultDescription),
      image: existingVariant.image || fallbackImages[0] || '',
      images: existingVariant.images.length ? existingVariant.images : fallbackImages,
      price: existingVariant.price ?? fallbackCard.price ?? null
    })
  }

  const normalizedOrder = colorOrder.map(entry => normalizeColorRoute(entry)).filter(Boolean)

  return Array.from(variantsByColor.values()).sort((left, right) => {
    const leftIndex = normalizedOrder.indexOf(left.color)
    const rightIndex = normalizedOrder.indexOf(right.color)

    if (leftIndex !== -1 || rightIndex !== -1) {
      if (leftIndex === -1) return 1
      if (rightIndex === -1) return -1
      return leftIndex - rightIndex
    }

    return left.color.localeCompare(right.color)
  })
}

export function buildCollectionStructuredData({
  brandName = 'Kendrick Paris',
  category,
  collectionDescription,
  collectionId,
  collectionName,
  priceValidUntil,
  selectedVariant,
  variants
}: BuildStructuredDataOptions) {
  if (!variants.length && !selectedVariant) return null

  if (selectedVariant) {
    return pruneStructuredData({
      '@context': 'https://schema.org',
      ...buildProductNode(selectedVariant, {
        brandName,
        category,
        priceValidUntil
      })
    })
  }

  return pruneStructuredData({
    '@context': 'https://schema.org',
    '@type': 'ProductGroup',
    productGroupID: collectionId || collectionName,
    name: collectionName,
    description: collectionDescription,
    brand: {
      '@type': 'Brand',
      name: brandName
    },
    category,
    url: variants[0]?.url?.split('?')[0] || undefined,
    variesBy: ['https://schema.org/color'],
    image: Array.from(new Set(variants.flatMap(variant => variant.images))).slice(0, 12),
    hasVariant: variants.map(variant =>
      buildProductNode(variant, {
        brandName,
        category,
        priceValidUntil
      })
    )
  })
}
